import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { useQueryClient } from "react-query";

import { BasePage } from "components/BasePage/BasePage";
import { SubscriptionStatusBanner } from "components/Billing/SubscriptionStatusBanner";
import { PlanSelector } from "components/Billing/PlanSelector";
import { PrimaryButton } from "components/Button/PrimaryButton";
import {
  useSubscription,
  useCancelSubscription,
  useOpenBillingPortal,
} from "hooks/useStripe";
import { ControlContext } from "context/Controls";
import { useConfirmation } from "context/Confirmation";

export const BillingPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { openConfirmation } = useConfirmation();

  const { subscription, isLoading, uiState } = useSubscription();
  const { cancelSubscription, isLoading: isCancelling } =
    useCancelSubscription();
  const { openBillingPortal, isLoading: isOpeningPortal } =
    useOpenBillingPortal();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("success") === "true") {
      queryClient.invalidateQueries(["stripe", "subscription"]);
      displayNotification!(t("settings.billing.checkout_success"), "", "success");
      history.replace("/settings/billing");
    } else if (params.get("canceled") === "true") {
      displayNotification!(t("settings.billing.checkout_canceled"), "", "error");
      history.replace("/settings/billing");
    }
  }, [location.search, displayNotification, history, queryClient, t]);

  const handleCancel = () => {
    openConfirmation(() => cancelSubscription(), {
      label: t("settings.billing.cancel_confirm_title"),
      subline: t("settings.billing.cancel_confirm_message"),
    });
  };

  return (
    <BasePage
      breadcrumbItems={[
        { label: t("settings.tab.billing"), location: "/settings" },
        {
          label: t("settings.tab.billing"),
          location: "/settings/billing",
        },
      ]}
      loading={isLoading}
    >
      <div className="space-y-8">
        <h2 className="text-lg font-medium text-gray-900">
          {t("settings.billing.title")}
        </h2>

        <SubscriptionStatusBanner subscription={subscription} />

        {(uiState === "free" || uiState === "trial_active") && (
          <PlanSelector
            currentPlan={subscription?.plan}
            currentFrequency={subscription?.frequency}
          />
        )}

        {(uiState === "active" || uiState === "cancelling") && (
          <div className="flex space-x-3">
            {uiState === "active" && (
              <PrimaryButton
                disabled={isCancelling}
                onClick={handleCancel}
              >
                {t("settings.billing.action_cancel")}
              </PrimaryButton>
            )}
            <PrimaryButton
              disabled={isOpeningPortal}
              onClick={() => openBillingPortal()}
            >
              {t("settings.billing.action_manage")}
            </PrimaryButton>
          </div>
        )}
      </div>
    </BasePage>
  );
};

export default BillingPage;
